import React from 'react';
import {
  ChatBubbleLeftRightIcon,
  CpuChipIcon,
  ClockIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  UserIcon,
  PaperAirplaneIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';

export default function SicknessReportHelp() {
  return (
    <div className="bg-gradient-to-br from-blue-50 to-purple-100 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <div className="bg-blue-600 p-3 rounded-full">
              <ChatBubbleLeftRightIcon className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Sickness Report Guide</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Learn how to use our AI-powered sickness reporting system for efficient,
            supportive, and confidential health absence reporting.
          </p>
        </div>

        {/* Overview Card */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <div className="flex items-center mb-6">
            <CpuChipIcon className="h-6 w-6 text-blue-600 mr-3" />
            <h2 className="text-2xl font-semibold text-gray-900">Overview</h2>
          </div>
          <p className="text-gray-700 text-lg leading-relaxed">
            The Sickness Report system provides a compassionate, AI-guided approach to reporting
            absences due to illness. Through a conversational interface, you'll answer targeted
            questions about your symptoms and situation, allowing our AI Health Assistant to
            provide appropriate guidance and ensure proper documentation of your absence.
          </p>
        </div>

        {/* How It Works */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <div className="flex items-center mb-6">
            <ArrowRightIcon className="h-6 w-6 text-blue-600 mr-3" />
            <h2 className="text-2xl font-semibold text-gray-900">How the Sickness Report Works</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-blue-600 font-bold text-lg">1</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Initiate Report</h3>
              <p className="text-gray-600 text-sm">Access the sickness reporting system when you're unable to work</p>
            </div>

            <div className="text-center">
              <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-blue-600 font-bold text-lg">2</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">AI-Guided Conversation</h3>
              <p className="text-gray-600 text-sm">Answer 6 targeted questions about your symptoms and situation</p>
            </div>

            <div className="text-center">
              <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-blue-600 font-bold text-lg">3</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Complete & Submit</h3>
              <p className="text-gray-600 text-sm">Review assessment and submit report with automatic notifications</p>
            </div>
          </div>
        </div>

        {/* Key Features */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Conversational Interface */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-4">
              <ChatBubbleLeftRightIcon className="h-6 w-6 text-blue-600 mr-3" />
              <h3 className="text-xl font-semibold text-gray-900">Conversational AI Interface</h3>
            </div>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>AI Health Assistant:</strong> Personalized guidance through your sickness reporting</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>6-Question Assessment:</strong> Structured evaluation of your symptoms and situation</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Real-time Responses:</strong> Immediate AI analysis and follow-up questions</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Progress Tracking:</strong> Visual indicator showing Question X/6 completion</span>
              </li>
            </ul>
          </div>

          {/* Privacy & Support */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-4">
              <ShieldCheckIcon className="h-6 w-6 text-blue-600 mr-3" />
              <h3 className="text-xl font-semibold text-gray-900">Privacy & Support</h3>
            </div>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Confidential Reporting:</strong> Secure, private communication of health information</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Compassionate Approach:</strong> Supportive language and understanding tone</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Medical Guidance:</strong> Appropriate advice about seeking professional medical help</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Automatic Notifications:</strong> HR and management notified appropriately</span>
              </li>
            </ul>
          </div>

          {/* Assessment Process */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-4">
              <UserIcon className="h-6 w-6 text-blue-600 mr-3" />
              <h3 className="text-xl font-semibold text-gray-900">Assessment Process</h3>
            </div>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Symptom Description:</strong> Detailed explanation of your symptoms and their impact</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Duration & Severity:</strong> How long symptoms have lasted and their intensity</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Medical Consultation:</strong> Whether you've seen or plan to see a healthcare professional</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Return Expectations:</strong> Anticipated timeline for returning to work</span>
              </li>
            </ul>
          </div>

          {/* Completion & Follow-up */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-4">
              <ClockIcon className="h-6 w-6 text-blue-600 mr-3" />
              <h3 className="text-xl font-semibold text-gray-900">Completion & Follow-up</h3>
            </div>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Assessment Summary:</strong> AI-generated summary of your health situation</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Wellness Wishes:</strong> Encouraging message and get-well-soon sentiment</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Email Notification:</strong> Automatic notification sent to relevant parties</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span><strong>Secure Logout:</strong> Automatic logout after report completion for security</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Sample Conversation Flow */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-8 mb-8 text-white">
          <div className="flex items-center mb-6">
            <ChatBubbleLeftRightIcon className="h-8 w-8 mr-3" />
            <h2 className="text-2xl font-semibold">Sample Conversation Flow</h2>
          </div>
          <div className="space-y-4">
            <div className="bg-white/10 rounded-lg p-4 border border-white/20">
              <div className="flex items-start space-x-3">
                <div className="bg-blue-500 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">🤖</div>
                <div>
                  <div className="font-semibold text-blue-200">AI Health Assistant:</div>
                  <div className="text-white mt-1">Why can't you attend work today? What are your symptoms?</div>
                </div>
              </div>
            </div>

            <div className="bg-white/10 rounded-lg p-4 border border-white/20">
              <div className="flex items-start space-x-3">
                <div className="bg-purple-500 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">👤</div>
                <div>
                  <div className="font-semibold text-purple-200">You:</div>
                  <div className="text-white mt-1">I have a high fever, severe headache, and body aches. I also have a sore throat and cough.</div>
                </div>
              </div>
            </div>

            <div className="bg-white/10 rounded-lg p-4 border border-white/20">
              <div className="flex items-start space-x-3">
                <div className="bg-blue-500 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">🤖</div>
                <div>
                  <div className="font-semibold text-blue-200">AI Health Assistant:</div>
                  <div className="text-white mt-1">How long have you been experiencing these symptoms?</div>
                </div>
              </div>
            </div>

            <div className="text-center text-blue-200 mt-4">
              <em>...continues through 6 questions...</em>
            </div>
          </div>
        </div>

        {/* Important Guidelines */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Important Guidelines for Sickness Reporting</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">🏥 Health & Medical Advice</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Be honest and detailed about your symptoms</li>
                <li>• Mention any pre-existing medical conditions</li>
                <li>• Include information about medications you're taking</li>
                <li>• Note if you've consulted a healthcare professional</li>
                <li>• Describe how symptoms affect your ability to work</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">📝 Reporting Best Practices</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Report as early as possible in your absence</li>
                <li>• Keep responses clear and concise</li>
                <li>• Answer all questions thoughtfully</li>
                <li>• Take your time - there's no rush</li>
                <li>• Complete the full assessment for best support</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">🔒 Privacy & Confidentiality</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Your health information is kept strictly confidential</li>
                <li>• Only authorized HR and management personnel have access</li>
                <li>• Information is used solely for absence management</li>
                <li>• Medical details are handled in compliance with privacy laws</li>
                <li>• You can request access to your own health records</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">📞 When to Seek Help</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Contact emergency services for severe symptoms</li>
                <li>• See a GP for symptoms lasting more than a few days</li>
                <li>• Inform your manager directly for urgent work matters</li>
                <li>• Reach out to HR for additional support needs</li>
                <li>• Use employee assistance programs when available</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Tips & Best Practices */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Tips for Effective Sickness Reporting</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">💬 Communication Tips</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Use clear, descriptive language for symptoms</li>
                <li>• Include timeline information when relevant</li>
                <li>• Mention any patterns you've noticed</li>
                <li>• Be specific about work limitations</li>
                <li>• Express appreciation for the support</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">📱 Mobile Usage</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Use portrait mode for better message readability</li>
                <li>• Take your time composing detailed responses</li>
                <li>• Ensure stable internet connection</li>
                <li>• Save drafts if you need to step away</li>
                <li>• Use voice-to-text for longer symptom descriptions</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">⏰ Timing & Follow-up</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Report sickness as soon as you know you'll be absent</li>
                <li>• Keep your manager updated on your condition</li>
                <li>• Submit fit notes from your GP when available</li>
                <li>• Update your return-to-work date as it becomes clearer</li>
                <li>• Contact HR if your absence extends beyond expectations</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">🏥 Self-Care Focus</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Prioritize rest and recovery</li>
                <li>• Stay hydrated and eat nutritious foods</li>
                <li>• Follow any medical advice received</li>
                <li>• Avoid returning to work too soon</li>
                <li>• Use this time for proper recuperation</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">🤝 Support Resources</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Access company employee assistance programs</li>
                <li>• Contact occupational health services if available</li>
                <li>• Reach out to HR for additional support</li>
                <li>• Discuss flexible working arrangements</li>
                <li>• Explore wellness and mental health resources</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">📋 Documentation</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Keep records of your symptoms and treatments</li>
                <li>• Save medical certificates and fit notes</li>
                <li>• Document communications with healthcare providers</li>
                <li>• Note any work-related adjustments discussed</li>
                <li>• Maintain personal health records for reference</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}